/**
 * Prøvesett steg 3 — Vurderingsmatrise + rammeverk (CX kompositt-veiviser).
 *
 * Trinn C (2026-11-01): Per-komponent rubric-velger + task-description-input.
 * Kompositt-flyt viser én rad per komponent. Enkelt-komponent (bakoverkompat)
 * bruker samme UI med én rad.
 */
import { useEffect, useState } from 'react';
import { Loader2, FileCheck, Info, Plus, AlertCircle, FileText } from 'lucide-react';
import { authenticatedFetch } from '../../../lib/auth';
import type { ExamSetWizardData, WizardComponentDraft } from '../NewExamSetWizard';
import InlineHelpTerm from '../InlineHelpTerm';

interface Rubric {
  id: string;
  title: string;
  description?: string;
  component_type?: string;
  locked_at?: string | null;
  version?: number;
}

export interface RubricStepProps {
  data: ExamSetWizardData;
  patch: (updates: Partial<ExamSetWizardData>) => void;
  /**
   * Åpner inline-oppretter. idx er komponent-index (CX) så nyopprettet
   * rubric_id tilordnes den riktige komponenten. Omitted = legacy-single.
   */
  onOpenCreateRubric?: (idx?: number) => void;
}

const COMPONENT_LABELS: Record<string, string> = {
  written: 'Skriftlig prøve',
  oral: 'Muntlig eksamen',
  project: 'Prosjektoppgave',
  practical: 'Praktisk demonstrasjon',
  portfolio: 'Mappevurdering',
  observation: 'Kontinuerlig observasjon',
  group: 'Gruppearbeid',
};

const COMPONENT_NEEDS_RUBRIC: Record<string, boolean> = {
  written: false,
  oral: true,
  project: true,
  practical: true,
  portfolio: true,
  observation: true,
  group: true,
};

const COMPONENT_HAS_TASK: Record<string, boolean> = {
  written: false,
  oral: false,
  project: true,
  practical: false,
  portfolio: true,
  observation: false,
  group: true,
};

export default function RubricStep({ data, patch, onOpenCreateRubric }: RubricStepProps) {
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedFetch('/api/admin/assessment-rubrics');
        const json = await res.json();
        if (res.status === 404 || res.status === 501) {
          setRubrics([]);
          return;
        }
        if (!json.success && json.error) throw new Error(json.error);
        const list = Array.isArray(json.rubrics) ? json.rubrics : Array.isArray(json) ? json : [];
        setRubrics(list);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Ukjent feil';
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function updateComponent(idx: number, patch_: Partial<WizardComponentDraft>) {
    const next = data.components.map((c, i) => i === idx ? { ...c, ...patch_ } : c);
    patch({ components: next });
  }

  function filterRubricsForType(compType: string): Rubric[] {
    return rubrics.filter(r => !r.component_type || r.component_type === compType);
  }

  // Enkelt-komponent-flyt: legacy single-rubric_id på root-nivå brukes fortsatt
  // ved innsending til backend (buildRequest i NewExamSetWizard mapper).
  // For UI er alt per-komponent nå.

  return (
    <div className="space-y-5">
      <div className="rounded-lg bg-kf-gray-50 border border-kf-gray-200 p-4 text-sm text-kf-gray-700 flex items-start gap-2">
        <Info className="w-4 h-4 text-kf-gray-500 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-kf-gray-800 mb-1">
            Hvorfor{' '}
            <InlineHelpTerm slug="wizard-rubric-matrix" label="vurderingsmatrise" route="/courses-and-exams" />?
          </p>
          <p>
            En matrise beskriver hvilke kriterier sensor vurderer etter og hvilket nivå kandidaten må nå. Matriser gir
            konsistent sensur og er påkrevd for ikke-skriftlige komponenter.
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-kf-gray-600 py-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Laster matriser…
        </div>
      )}

      {error && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Kunne ikke laste matriser: {error}</span>
        </div>
      )}

      {/* Per-komponent-rader */}
      {!loading && data.components.map((comp, idx) => {
        const label = COMPONENT_LABELS[comp.component_type] || comp.component_type;
        const needsRubric = COMPONENT_NEEDS_RUBRIC[comp.component_type];
        const hasTask = COMPONENT_HAS_TASK[comp.component_type];
        const available = filterRubricsForType(comp.component_type);
        const rubricMissing = needsRubric && !comp.rubric_id;

        return (
          <div key={`${comp.component_type}-${idx}`} className={`rounded-lg border p-4 ${
            rubricMissing ? 'border-red-300 bg-red-50' : 'border-kf-gray-200 bg-white'
          }`}>
            <div className="font-semibold text-kf-gray-900 text-sm mb-3 flex items-center gap-2">
              <FileCheck size={16} className="text-kf-blue-600" />
              {label}
              {needsRubric && <span className="text-xs font-normal text-red-600">(matrise påkrevd)</span>}
            </div>

            {/* Rubric-velger */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-kf-gray-700">
                {needsRubric ? 'Velg vurderingsmatrise *' : 'Vurderingsmatrise (valgfri)'}
              </label>
              {available.length === 0 ? (
                <div className="rounded-md bg-kf-blue-50 border border-kf-blue-200 px-3 py-2 text-sm text-kf-blue-900">
                  Ingen eksisterende matriser for {label.toLowerCase()}. {onOpenCreateRubric
                    ? 'Opprett ny under.'
                    : 'Du må opprette en matrise i Vurderingsmatriser-menyen før du kan fortsette.'}
                </div>
              ) : (
                <select
                  value={comp.rubric_id ?? ''}
                  onChange={e => updateComponent(idx, { rubric_id: e.target.value || null })}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500 ${
                    rubricMissing ? 'border-red-400' : 'border-kf-gray-300'
                  }`}
                >
                  <option value="">— {needsRubric ? 'Velg matrise' : 'Ingen matrise'} —</option>
                  {available.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.title}{r.version ? ` (v${r.version})` : ''}{r.locked_at ? ' · låst' : ''}
                    </option>
                  ))}
                </select>
              )}
              {onOpenCreateRubric && (
                <button
                  type="button"
                  onClick={() => onOpenCreateRubric(idx)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-kf-blue-700 hover:bg-kf-blue-50 rounded border border-kf-blue-200"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Opprett ny matrise for {label.toLowerCase()}
                </button>
              )}
              {rubricMissing && (
                <div className="text-xs text-red-700">Må velges før du kan gå videre.</div>
              )}
            </div>

            {/* Task-description for innleverings-komponenter */}
            {hasTask && (
              <div className="mt-3 space-y-2">
                <label className="block text-xs font-medium text-kf-gray-700">
                  <FileText className="inline w-3.5 h-3.5 mr-1" />
                  Standard oppgavetekst (kan overstyres per kursinstans)
                </label>
                <textarea
                  value={comp.task_description}
                  onChange={e => updateComponent(idx, { task_description: e.target.value })}
                  rows={3}
                  placeholder={`Beskriv ${label.toLowerCase()}-oppgaven kandidater skal løse. Kursarrangør kan overstyre per kjøring.`}
                  className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500 text-sm"
                />
                <div className="space-y-2 text-xs text-kf-gray-600">
                  <label className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">Grace-buffer:</span>
                    <input type="number" min={0} max={720}
                      value={comp.grace_period_hours}
                      onChange={e => updateComponent(idx, { grace_period_hours: Math.max(0, parseInt(e.target.value) || 0) })}
                      className="w-16 px-2 py-0.5 border border-kf-gray-300 rounded" />
                    <span>timer etter oppgitt frist</span>
                    <span className="block w-full text-xs text-kf-gray-500 italic pl-1">
                      Buffer for sene SMTP-leveringer, tekniske forsinkelser og tidssone-forskjeller.
                      Fristen selv settes per kursinstans (ikke her — her bare standard-bufferen).
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <span className="font-medium">Maks filer per innlevering:</span>
                    <input type="number" min={1} max={50}
                      value={comp.max_files_per_submission}
                      onChange={e => updateComponent(idx, { max_files_per_submission: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="w-16 px-2 py-0.5 border border-kf-gray-300 rounded" />
                  </label>
                </div>
              </div>
            )}

            {/* Anonymitets-toggle */}
            <div className="mt-3 rounded border border-kf-gray-200 bg-kf-gray-50 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={comp.is_anonymous}
                  onChange={e => updateComponent(idx, { is_anonymous: e.target.checked })} />
                <span className="flex-1">
                  <span className="font-medium text-sm text-kf-gray-900 block">
                    Anonym vurdering (sensor ser ikke kandidatens navn)
                  </span>
                  <span className="text-xs text-kf-gray-600 block mt-1">
                    Sensor får bare kandidatens anonyme kode — navn og identitet er skjult til etter karaktersetting.
                    Dette gir mer rettferdig vurdering og reduserer risiko for ubevisst forskjellsbehandling.
                  </span>
                  <span className="text-xs text-kf-gray-500 block mt-1 italic">
                    Anbefalt for: skriftlige prøver, prosjektoppgaver, mappevurdering.
                    Ikke relevant for: muntlig, praktisk, observasjon, gruppearbeid (sensor må møte kandidaten).
                  </span>
                </span>
              </label>
            </div>
          </div>
        );
      })}

      {/* Rammeverk-begrunnelse (uendret) */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">
          Begrunnelse (hvis vurderingsrammen krever det)
        </label>
        <textarea
          value={data.framework_rationale}
          onChange={e => patch({ framework_rationale: e.target.value })}
          rows={3}
          placeholder="Fyll ut hvis studieplanens vurderingsramme krever begrunnelse for valg av matrise, assessor-type osv…"
          className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
        />
        <p className="text-xs text-kf-gray-500 mt-1">
          Full kompatibilitets-sjekk kjøres ved lagring.
        </p>
      </div>
    </div>
  );
}
